import { createDataLoader } from '../batching/data-loader'
import {
  createRetryFetch,
  type FetchFn,
  type OnlineDetector,
  type RetryFetchOptions,
} from '../retry'

export interface OperationTransportContext {
  fetch: FetchFn
}

export interface OperationBatchingOptions<TOperation, TResult> {
  enabled?: boolean
  timeoutMs?: number
  groupBy?: (operation: TOperation) => string
  shouldBatch?: (operation: TOperation) => boolean
  validate?: (operations: TOperation[]) => boolean
  executeBatch: (
    operations: TOperation[],
    ctx: OperationTransportContext,
  ) => Promise<TResult[] | Promise<TResult>[]>
}

export interface OperationTransportOptions<TOperation, TResult> {
  fetch?: FetchFn
  retry?: RetryFetchOptions | false
  onlineDetector?: OnlineDetector
  execute: (
    operation: TOperation,
    ctx: OperationTransportContext,
  ) => Promise<TResult>
  batching?: OperationBatchingOptions<TOperation, TResult>
}

export interface OperationTransport<TOperation, TResult> {
  fetch: FetchFn
  execute(operation: TOperation): Promise<TResult>
}

export function createOperationTransport<TOperation, TResult>(
  options: OperationTransportOptions<TOperation, TResult>,
): OperationTransport<TOperation, TResult> {
  const fetch = resolveTransportFetch(options)
  const ctx: OperationTransportContext = { fetch }
  const batching = options.batching
  const loaders = new Map<
    string,
    ReturnType<typeof createDataLoader<TOperation, TResult>>
  >()

  function getLoader(operation: TOperation) {
    const key = batching?.groupBy?.(operation) ?? 'default'
    const existing = loaders.get(key)
    if (existing) return existing

    const loader = createDataLoader<TOperation, TResult>(
      {
        validate: (operations) => batching?.validate?.(operations) ?? true,
        fetch: (operations) => {
          if (!batching) {
            throw new Error('Batching unexpectedly missing from transport.')
          }
          return batching.executeBatch(operations, ctx)
        },
      },
      { timeoutMs: batching?.timeoutMs },
    )

    loaders.set(key, loader)
    return loader
  }

  return {
    fetch,
    execute(operation) {
      if (!batching?.enabled) {
        return options.execute(operation, ctx)
      }

      if (batching.shouldBatch && !batching.shouldBatch(operation)) {
        return options.execute(operation, ctx)
      }

      return getLoader(operation).load(operation)
    },
  }
}

function resolveTransportFetch<TOperation, TResult>(
  options: OperationTransportOptions<TOperation, TResult>,
): FetchFn {
  const rawFetch = options.fetch ?? fetch
  const baseFetch: FetchFn = (input, init) =>
    rawFetch.call(globalThis, input, init)
  const retryOptions = options.retry

  if (retryOptions === false && options.onlineDetector === undefined) {
    return baseFetch
  }

  if (retryOptions === undefined && options.onlineDetector === undefined) {
    return baseFetch
  }

  const resolvedRetryOptions: RetryFetchOptions =
    retryOptions === false
      ? { retries: 0 }
      : retryOptions
        ? { ...retryOptions }
        : {}

  if (options.onlineDetector !== undefined) {
    resolvedRetryOptions.onlineDetector = options.onlineDetector
  }

  return createRetryFetch(baseFetch, resolvedRetryOptions)
}
